import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import db from "./Firebase";
import { doc, updateDoc, collection, onSnapshot } from "firebase/firestore";
import Moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ViewFormModal from "./ViewFormModal";

const moment = Moment;

function RegistrationFormList() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [remarks, setRemarks] = useState({});
    const [modalShow, setModalShow] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "registration_form_data"),
            (snapshot) => {
                const sortedData = snapshot.docs
                    .map((docSnap) => ({
                        id: docSnap.id,
                        data: docSnap.data(),
                    }))
                    .sort((a, b) => {
                        const aTime = a.data.createdAt?.toDate().getTime() || 0;
                        const bTime = b.data.createdAt?.toDate().getTime() || 0;
                        return bTime - aTime;
                    });
                setData(sortedData);
                console.log(sortedData)

                const initialRemarks = {};
                sortedData.forEach(({ id, data }) => {
                    initialRemarks[id] = data.remarks || "";
                });
                setRemarks(initialRemarks);
            },
            (error) => {
                console.error("Error fetching data:", error);
            }
        );

        return () => unsubscribe();
    }, []);

    const handleSearch = (e) => setSearchQuery(e.target.value);

    // const handleStatusChange = async (id, newStatus) => {
    //     try {
    //         const docRef = doc(db, "registration_form_data", id);
    //         await updateDoc(docRef, { status: newStatus });
    //         Swal.fire("Success", "Status updated!", "success");
    //     } catch (error) {
    //         console.error("Error updating status:", error);
    //         Swal.fire("Error", "Failed to update!", "error");
    //     }
    // };


    const handleStatusChange = async (id, newStatus) => {
        const docRef = doc(db, "registration_form_data", id);

        // 🟥 When Reject is selected → show SweetAlert with textarea
        if (newStatus === "Reject") {
            const { value: remarkText } = await Swal.fire({
                title: "Reject Application",
                input: "textarea",
                inputLabel: "Please enter reason for rejection",
                inputPlaceholder: "Type your remark here...",
                inputAttributes: {
                    "aria-label": "Type your remark here",
                },
                showCancelButton: true,
                confirmButtonText: "Submit",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d",
                inputValidator: (value) => {
                    if (!value) {
                        return "Please enter a remark before submitting!";
                    }
                },
            });

            // ✅ If user submits
            if (remarkText) {
                try {
                    await updateDoc(docRef, { status: newStatus, remarks: remarkText });
                    Swal.fire("Rejected!", "Application has been rejected.", "success");
                } catch (error) {
                    console.error("Error updating status:", error);
                    Swal.fire("Error", "Failed to reject application.", "error");
                }
            }

            // ❌ If user cancels → reset dropdown visually (optional)
            else {
                Swal.fire("Cancelled", "No changes were made.", "info");
            }

            return; // exit function after handling Reject
        }

        // ✅ For Approved / Pending → update directly
        try {
            await updateDoc(docRef, { status: newStatus });
            Swal.fire("Success", `Status updated to ${newStatus}!`, "success");
        } catch (error) {
            console.error("Error updating status:", error);
            Swal.fire("Error", "Failed to update status!", "error");
        }
    };



    const handleRemarksChange = (id, value) => {
        setRemarks((prev) => ({ ...prev, [id]: value }));
    };

    const saveRemarks = async (id) => {
        try {
            const docRef = doc(db, "registration_form_data", id);
            await updateDoc(docRef, { remarks: remarks[id] });
            Swal.fire("Success", "Remarks updated!", "success");
        } catch (error) {
            console.error("Error updating remarks:", error);
            Swal.fire("Error", "Failed to update!", "error");
        }
    };

    const handleView = (record) => {
        setSelectedRecord(record);
        setModalShow(true);
    };

    // 🔍 Filter by phone number
    const filteredData = data.filter(({ data: d }) => {
        const phone = d.contact1 || "";
        return phone.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // 📥 DOWNLOAD EXCEL HANDLER
    // 📥 DOWNLOAD EXCEL HANDLER (Dynamic)
    const handleDownloadExcel = () => {
        if (!data.length) {
            Swal.fire("No Data", "No records available to export.", "info");
            return;
        }

        // 1️⃣ Flatten and merge all keys dynamically
        const allKeys = new Set();
        data.forEach(({ data: d }) => {
            Object.keys(d).forEach((key) => allKeys.add(key));
        });

        // 2️⃣ Convert Firestore objects to plain rows
        const exportData = data.map(({ id, data: d }, index) => {
            const row = { "Sr No": index + 1, "Document ID": id };
            allKeys.forEach((key) => {
                let value = d[key];

                // Handle arrays (like documents)
                if (Array.isArray(value)) {
                    value = value.join(", ");
                }

                // Handle Firestore timestamps
                if (value && typeof value === "object" && value.seconds) {
                    try {
                        const dateObj = new Date(value.seconds * 1000);
                        value = Moment(dateObj).format("YYYY-MM-DD HH:mm:ss");
                    } catch {
                        value = JSON.stringify(value);
                    }
                }

                row[key] = value ?? "";
            });
            return row;
        });

        // 3️⃣ Create Excel Sheet
        const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: "A1" });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "RegistrationData");

        // 4️⃣ Download File
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `registration_form_data_${Moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    return (
        <>
            <div style={{ padding: "1rem" }}>
                <div
                    className="p-2"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <h5 className="mb-0">All Registration Data (Count: {data.length})</h5>

                    {/* ✅ DOWNLOAD BUTTON */}
                    <button
                        onClick={handleDownloadExcel}
                        className="btn btn-success btn-sm"
                        style={{ whiteSpace: "nowrap" }}
                    >
                        📥 Download Excel
                    </button>
                </div>

                <div className="p-3">
                    <input
                        type="text"
                        className="form-control form-control-sm mb-3"
                        placeholder="Search by phone no..."
                        value={searchQuery}
                        onChange={handleSearch}
                    />

                    <div className="table-responsive fix-width scroll-inner">
                        <table className="table table-bordered table-hover">
                            <thead className="thead-dark" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                    <th>Sr. No</th>
                                    <th>Name</th>
                                    <th>Phone No</th>
                                    <th>Created Date</th>
                                    <th>Status</th>
                                    <th>Remarks</th>
                                    <th>View Form</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map(({ id, data: d }, index) => (
                                    <tr key={id}>
                                        <td>{index + 1}</td>
                                        <td>{d.applicantName || ""}</td>
                                        <td>{d.contact1 || ""}</td>
                                        <td>
                                            {d.createdAt
                                                ? moment(d.createdAt.toDate()).format("DD/MM/YY, h:mm a")
                                                : "-"}
                                        </td>
                                        <td>
                                            {d.status === "Approved" ?
                                                <span
                                                    className={`badge ${d.status === "Approved"
                                                        ? "bg-success"
                                                        : d.status === "Pending"
                                                            ? "bg-warning text-dark"
                                                            : "bg-secondary"
                                                        }`}
                                                    style={{ fontSize: "0.85rem", padding: "6px 10px" }}
                                                >
                                                    {d.status || "Pending"}
                                                </span>
                                                :
                                                <select
                                                    className="form-control"
                                                    value={d.status || "Pending"}
                                                    onChange={(e) => handleStatusChange(id, e.target.value)}

                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Reject">Reject</option>
                                                    <option value="Approved">Approved</option>
                                                </select>
                                            }

                                        </td>

                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <textarea
                                                    className="form-control"
                                                    placeholder="Enter remarks..."
                                                    style={{ resize: "both", minHeight: "40px", height: "40px" }}
                                                    value={remarks[id] || ""}
                                                    onChange={(e) => handleRemarksChange(id, e.target.value)}
                                                />
                                                {/* <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => saveRemarks(id)}
                                                >
                                                    Save
                                                </button> */}
                                            </div>
                                        </td>

                                        <td>
                                            <button
                                                className="btn btn-sm btn-info"
                                                onClick={() => handleView({ id, data: d })}
                                            >
                                                View Form
                                            </button>
                                            {d.status === "Approved" ? <>     <Link target="_blank" to={`/form.html?id=${id}`}
                                                className="btn btn-sm btn-success text-white ml-3"

                                            >
                                                Download PDF
                                            </Link></> : <></>}

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <ViewFormModal
                show={modalShow}
                record={selectedRecord}
                onClose={() => {
                    setModalShow(false);
                    setSelectedRecord(null);
                }}
            />
        </>
    );
}

export default RegistrationFormList;



// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import Swal from "sweetalert2";
// import db from "./Firebase";
// import { doc, updateDoc, collection, onSnapshot } from "firebase/firestore";
// import Moment from "moment";
// import ViewFormModal from "./ViewFormModal"; // ✅ this should be here, not inside the component or after code

// const moment = Moment; // or extend if needed



// function RegistrationFormList() {
//     const [data, setData] = useState([]);
//     const [searchQuery, setSearchQuery] = useState("");
//     const [remarks, setRemarks] = useState({});

//     const [modalShow, setModalShow] = useState(false);
//     const [selectedRecord, setSelectedRecord] = useState(null);

//     useEffect(() => {
//         const unsubscribe = onSnapshot(
//             collection(db, "registration_form_data"),
//             (snapshot) => {
//                 const sortedData = snapshot.docs.map(docSnap => ({
//                     id: docSnap.id,
//                     data: docSnap.data()
//                 })).sort((a, b) => {
//                     const aTime = a.data.createdAt?.toDate().getTime() || 0;
//                     const bTime = b.data.createdAt?.toDate().getTime() || 0;
//                     return bTime - aTime;
//                 });
//                 setData(sortedData);

//                 const initialRemarks = {};
//                 sortedData.forEach(({ id, data }) => {
//                     initialRemarks[id] = data.remarks || "";
//                 });
//                 setRemarks(initialRemarks);
//             },
//             (error) => {
//                 console.error("Error fetching data:", error);
//             }
//         );

//         return () => unsubscribe();
//     }, []);

//     const handleSearch = (e) => {
//         setSearchQuery(e.target.value);
//     };

//     const handleStatusChange = async (id, newStatus) => {
//         try {
//             const docRef = doc(db, "registration_form_data", id);
//             await updateDoc(docRef, { status: newStatus });
//             Swal.fire("Success", "Status updated!", "success");
//         } catch (error) {
//             console.error("Error updating status:", error);
//             Swal.fire("Error", "Failed to update!", "error");
//         }
//     };

//     const handleRemarksChange = (id, value) => {
//         setRemarks(prev => ({ ...prev, [id]: value }));
//     };

//     const saveRemarks = async (id) => {
//         try {
//             const docRef = doc(db, "registration_form_data", id);
//             await updateDoc(docRef, { remarks: remarks[id] });
//             Swal.fire("Success", "Remarks updated!", "success");
//         } catch (error) {
//             console.error("Error updating remarks:", error);
//             Swal.fire("Error", "Failed to update!", "error");
//         }
//     };

//     const handleView = (record) => {
//         setSelectedRecord(record);
//         setModalShow(true);
//     };

//     const filteredData = data.filter(({ data: d }) => {
//         const phone = d.contact1 || "";
//         return phone.toLowerCase().includes(searchQuery.toLowerCase());
//     });

//     return (
//         <>
//             <div style={{ padding: "1rem" }}>
//                 <div className="p‑2" style={{ display: "flex", justifyContent: "space‑between" }}>
//                     {/* <Link to="/">
//                     <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
//                 </Link> */}
//                 </div>

//                 <div className="p‑3">
//                     <div className="card-header pb‑0 px‑3">
//                         <h6 className="mb‑2">All Registration Data ‑ (Count: {data.length})</h6>
//                     </div>

//                     <input
//                         type="text"
//                         className="form-control form-control-sm mb‑2"
//                         placeholder="Search by phone no..."
//                         value={searchQuery}
//                         onChange={handleSearch}
//                     />

//                     <div className="table-responsive fix‑width scroll‑inner">
//                         <table className="table table-bordered table-hover">
//                             <thead className="thead-dark" style={{ position: "sticky", top: 0, zIndex: 1 }}>
//                                 <tr>
//                                     <th>Sr. No</th>
//                                     <th>Name</th>
//                                     <th>Phone No</th>
//                                     <th>Created Date</th>
//                                     <th>Status</th>
//                                     <th>Remarks</th>
//                                     <th>View Form</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {filteredData.map(({ id, data: d }, index) => (
//                                     <tr key={id}>
//                                         <td>{index + 1}</td>
//                                         <td>{d.applicantName || ""}</td>
//                                         <td>{d.contact1 || ""}</td>
//                                         <td>{d.createdAt ? moment(d.createdAt.toDate()).format("DD/MM/YY, h:mm a") : "-"}</td>
//                                         <td>
//                                             <select
//                                                 className="form-control"
//                                                 value={d.status || "Pending"}
//                                                 onChange={(e) => handleStatusChange(id, e.target.value)}
//                                             >
//                                                 <option value="Pending">Pending</option>
//                                                 <option value="Reject">Reject</option>
//                                                 <option value="Approved">Approved</option>
//                                             </select>
//                                         </td>
//                                         <td>
//                                             <div className="d-flex align-items-center gap-2">
//                                                 <textarea
//                                                     className="form-control"
//                                                     placeholder="Enter remarks..."
//                                                     style={{ resize: "both", minHeight: "40px" }}
//                                                     value={remarks[id] || ""}
//                                                     onChange={(e) => handleRemarksChange(id, e.target.value)}
//                                                     onKeyDown={(e) => e.key === "Enter" && saveRemarks(id)}
//                                                 />
//                                                 <button
//                                                     className="btn btn-sm btn-primary"
//                                                     onClick={() => saveRemarks(id)}
//                                                     style={{ whiteSpace: "nowrap", }}
//                                                 >
//                                                     Save
//                                                 </button>
//                                             </div>
//                                         </td>


//                                         <td>

//                                             <button className="btn btn-sm btn-info" onClick={() => handleView({ id, data: d })}>
//                                                 View Form
//                                             </button>
//                                         </td>
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </table>
//                     </div>
//                 </div>
//             </div>

//             <ViewFormModal
//                 show={modalShow}
//                 record={selectedRecord}
//                 onClose={() => {
//                     setModalShow(false);
//                     setSelectedRecord(null);
//                 }}
//             />
//         </>
//     );
// }

// export default RegistrationFormList;


// // import React, { useState, useEffect } from "react";



// // import { Link } from "react-router-dom";
// // import Swal from "sweetalert2";
// // import db from "./Firebase";
// // import { doc, updateDoc, collection, onSnapshot } from "firebase/firestore";
// // import Moment from "moment";
// // import { extendMoment } from "moment-range";

// // function RegistrationFormList() {
// //     const moment = extendMoment(Moment);
// //     const [data, setData] = useState([]);
// //     const [searchQuery, setSearchQuery] = useState("");
// //     const [remarks, setRemarks] = useState({}); // Local state for remarks

// //     useEffect(() => {
// //         const unsubscribe = onSnapshot(
// //             collection(db, "registration_form_data"),
// //             (snapshot) => {
// //                 const sortedData = snapshot.docs
// //                     .map((doc) => ({ id: doc.id, data: doc.data() }))
// //                     .sort((a, b) => b.data.createdAt - a.data.createdAt);
// //                 setData(sortedData);

// //                 // Initialize remarks state with existing remarks from Firestore
// //                 const initialRemarks = {};
// //                 sortedData.forEach(({ id, data }) => {
// //                     initialRemarks[id] = data.remarks || "";
// //                 });
// //                 setRemarks(initialRemarks);
// //             }
// //         );

// //         return () => unsubscribe();
// //     }, []);

// //     // Handle Search Input
// //     const handleSearch = (e) => {
// //         setSearchQuery(e.target.value);
// //     };

// //     // Handle Status Update
// //     const handleStatusChange = async (id, newStatus) => {
// //         try {
// //             const docRef = doc(db, "registration_form_data", id);
// //             await updateDoc(docRef, { status: newStatus });

// //             Swal.fire("Success", "Status updated!", "success");
// //         } catch (error) {
// //             console.error("Error updating status:", error);
// //             Swal.fire("Error", "Failed to update!", "error");
// //         }
// //     };

// //     // Handle Remarks Change (Local)
// //     const handleRemarksChange = (id, value) => {
// //         setRemarks((prev) => ({ ...prev, [id]: value }));
// //     };

// //     // Save Remarks to Firestore (on Enter or Save Click)
// //     const saveRemarks = async (id) => {
// //         try {
// //             const docRef = doc(db, "registration_form_data", id);
// //             await updateDoc(docRef, { remarks: remarks[id] });

// //             Swal.fire("Success", "Remarks updated!", "success");
// //         } catch (error) {
// //             console.error("Error updating remarks:", error);
// //             Swal.fire("Error", "Failed to update!", "error");
// //         }
// //     };

// //     return (
// //         <>
// //             <div className="p-2" style={{ display: "flex", justifyContent: "space-between" }}>
// //                 <Link to="/">
// //                     <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
// //                 </Link>
// //             </div>

// //             <div className="p-3">
// //                 <div className="card-header pb-0 px-3">
// //                     <h6 className="mb-2">All Registration Data - (Count: {data.length})</h6>
// //                 </div>

// //                 {/* Search Input */}
// //                 <input
// //                     type="text"
// //                     className="form-control form-control-sm mb-2"
// //                     placeholder="Search by phone no..."
// //                     value={searchQuery}
// //                     onChange={handleSearch}
// //                 />

// //                 <div className="table-responsive fix-width scroll-inner">
// //                     <table className="table table-bordered table-hover">
// //                         <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
// //                             <tr>
// //                                 <th>Sr. No</th>
// //                                 <th>Name</th>
// //                                 <th>Phone No</th>
// //                                 <th>Created Date</th>
// //                                 <th>Status</th>
// //                                 <th>Remarks</th>
// //                                 <th>Actions</th>
// //                             </tr>
// //                         </thead>
// //                         <tbody>
// //                             {data
// //                                 .filter((record) => record.data.contact1.includes(searchQuery))
// //                                 .map(({ id, data }, index) => (
// //                                     <tr key={id}>
// //                                         <td>{index + 1}</td>
// //                                         <td>{data.applicantName}</td>
// //                                         <td>{data.contact1}</td>
// //                                         <td>{moment(data.createdAt.toDate()).format("DD/MM/YY, h:mm a")}</td>

// //                                         {/* Status Dropdown */}
// //                                         <td>
// //                                             <select
// //                                                 className="form-control"
// //                                                 value={data.status || "Pending"}
// //                                                 onChange={(e) => handleStatusChange(id, e.target.value)}
// //                                             >
// //                                                 <option value="Pending">Pending</option>
// //                                                 <option value="Approved">Approved</option>
// //                                             </select>
// //                                         </td>

// //                                         {/* Remarks Input (Local State) */}
// //                                         <td>
// //                                             <input
// //                                                 type="text"
// //                                                 className="form-control"
// //                                                 placeholder="Enter remarks..."
// //                                                 value={remarks[id] || ""}
// //                                                 onChange={(e) => handleRemarksChange(id, e.target.value)}
// //                                                 onKeyDown={(e) => e.key === "Enter" && saveRemarks(id)}
// //                                             />
// //                                         </td>

// //                                         {/* Save Button */}
// //                                         <td>
// //                                             <button className="btn btn-sm btn-primary" onClick={() => saveRemarks(id)}>
// //                                                 Save
// //                                             </button>
// //                                         </td>
// //                                     </tr>
// //                                 ))}
// //                         </tbody>
// //                     </table>
// //                 </div>
// //             </div>
// //         </>
// //     );
// // }

// // export default RegistrationFormList;
